import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import { t } from 'i18next'
import NavBar from './components/NavBar/NavBar'
import Loading from 'Components/Loading'
import ProductDetail from './components/ProductDetail/ProductDetail'
import MediaScreensaver from './components/MediaScreensaver'
import { useCustomerDisplay } from './context/CustomerDisplayContext'
import { validatorCompany } from './utils/validatorCompany'
import './CustomerDisplayLayout.css'

const isDocumentFullScreen = () =>
  !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.msFullscreenElement
  )

const requestDocumentFullScreen = () => {
  if (isDocumentFullScreen()) return Promise.resolve(true)

  const elem = document.documentElement
  const request =
    elem.requestFullscreen ||
    elem.webkitRequestFullscreen ||
    elem.msRequestFullscreen

  if (!request) return Promise.resolve(false)

  return Promise.resolve(request.call(elem))
    .then(() => true)
    .catch(err => {
      console.warn('No se pudo poner en pantalla completa:', err)
      return false
    })
}

export default function CustomerDisplayLayout({ children }) {
  const location = useLocation()
  const {
    company,
    isChangeCompany,
    postToMainApp,
    isProductDetailOpen,
    setIsProductDetailOpen,
    productDetail,
    isSleepMode,
  } = useCustomerDisplay()
  const companySelected = validatorCompany(company)
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(
    () => !isDocumentFullScreen()
  )
  const sleepModeCountRef = useRef(0)

  // Increment counter each time sleep mode is activated to force fresh content
  useEffect(() => {
    if (isSleepMode) {
      sleepModeCountRef.current += 1
    }
  }, [isSleepMode])

  const enterFullScreen = useCallback(async () => {
    const didEnterFullscreen = await requestDocumentFullScreen()
    setShowFullscreenPrompt(!didEnterFullscreen && !isDocumentFullScreen())
  }, [])

  const handleFullscreenPrompt = useCallback(
    event => {
      event?.preventDefault()
      event?.stopPropagation?.()
      enterFullScreen()
    },
    [enterFullScreen]
  )

  useEffect(() => {
    postToMainApp({ customerDisplayReady: true })
  }, [postToMainApp])

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullScreen = isDocumentFullScreen()
      setShowFullscreenPrompt(!isFullScreen)

      // Notify main app when entering fullscreen
      if (isFullScreen) {
        postToMainApp({ isFullScreen: true })
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)

    // Check initial state
    handleFullscreenChange()

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      )
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }, [postToMainApp])

  // Fullscreen enter logic - triggers on click or keydown inside this window.
  useEffect(() => {
    const enterFullscreen = event => {
      if (event.type === 'click' || event.key === 'Enter') {
        enterFullScreen()
      }
    }

    document.addEventListener('click', enterFullscreen)
    document.addEventListener('keydown', enterFullscreen)

    return () => {
      document.removeEventListener('click', enterFullscreen)
      document.removeEventListener('keydown', enterFullscreen)
    }
  }, [enterFullScreen])

  // Check if currently on the idle/background route
  const isIdleRoute = location.pathname === '/customer_display'

  // Handle product detail modal close
  const handleCloseProductDetail = () => {
    postToMainApp({
      isProductDetail: false,
      isCloseDetailProduct: true,
    })
    setIsProductDetailOpen(false)
  }

  // Render content based on company change loading state
  if (isChangeCompany) {
    return <Loading full />
  }

  return (
    <>
      <div className="layout" data-testid="container-customer-display-main">
        {!isIdleRoute && (
          <div className="navbar-fixed" data-testid="container-navbar-main">
            <NavBar company={companySelected} />
          </div>
        )}
        <div className="content" data-testid="container-main-content">
          {children}
        </div>
      </div>

      {/* Fullscreen prompt modal */}
      <Modal
        show={showFullscreenPrompt && !isDocumentFullScreen()}
        centered
        animation={false}
        backdrop="static"
        keyboard={false}
        className="container-modal-full-screen customer-display-fullscreen-modal"
        backdropClassName="customer-display-fullscreen-backdrop"
        data-testid="modal-full-screen">
        <div
          className="customer-display-fullscreen-prompt"
          role="button"
          tabIndex={0}
          onClick={handleFullscreenPrompt}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              handleFullscreenPrompt(event)
            }
          }}>
          <h1 data-testid="text-full-screen-title">
            {t('CUSTOMER_DISPLAY.FULLSCREEN_TITLE')}
          </h1>
          <p data-testid="text-full-screen-message">
            {t('CUSTOMER_DISPLAY.MESSAGE_FULL_SCREEN')}
          </p>
        </div>
      </Modal>

      {/* Product Detail Modal */}
      <Modal
        show={isProductDetailOpen}
        fullscreen={true}
        onHide={handleCloseProductDetail}
        className="modal-product-detail-fullscreen"
        data-testid="modal-product-detail-fullscreen">
        <div
          className="layout"
          data-testid="container-customer-display-layout-modal">
          <div className="navbar-fixed" data-testid="container-navbar-fixed">
            <NavBar company={companySelected} />
          </div>
          <div
            className="content"
            data-testid="container-product-detail-content">
            {productDetail && (
              <ProductDetail
                product={productDetail}
                handleCloseModal={handleCloseProductDetail}
              />
            )}
          </div>
        </div>
      </Modal>

      {/* Sleep Mode Overlay - shows media screensaver when inactive */}
      {isSleepMode && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
          }}
          data-testid="sleep-mode-overlay">
          <MediaScreensaver key={sleepModeCountRef.current} />
        </div>
      )}
    </>
  )
}
